import { useEffect } from 'react';

import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon } from '@chakra-ui/icons';

import { useAppSelector, useAppDispatch } from 'hooks';

import getValidationMessage from 'helpers/getValidationMessage';

import {
  changePolicyInformation,
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addOrUpdateRemark,
  removeRemark,
} from 'reducers/autoPersonalFormReducer';

import {
  SECTIONS,
  MULTI_LINE_DISCOUNT_VALIDATION_ID,
  MULTI_LINE_DISCOUNT_ITEM_KEY,
  PROPERTY_BINDER_NUMBER_LABEL,
  PROPERTY_BINDER_NUMBER_PATTERN,
} from 'views/auto_form/constants';

const PropertyBinderNumberField = ({
  validations,
}: {
  validations: Validation[];
}) => {
  const dispatch = useAppDispatch();

  const value = useAppSelector(
    (state) =>
      state.autoPersonalForm.data.policy_information.property_binder_number ??
      '',
  );

  const isValid = PROPERTY_BINDER_NUMBER_PATTERN.test(value);
  const isInvalid = value.length > 0 && !isValid;

  useEffect(() => {
    if (isValid) {
      dispatch(removeMissingMandatoryField(PROPERTY_BINDER_NUMBER_LABEL));
      dispatch(
        addOrUpdateRemark({
          validation_key: MULTI_LINE_DISCOUNT_VALIDATION_ID,
          item_key: MULTI_LINE_DISCOUNT_ITEM_KEY,
          data: {
            text: `Multi-line Discount: Property Binder Number: ${value}`,
            section: SECTIONS.POLICY_INFORMATION,
          },
        }),
      );
    } else {
      dispatch(
        addMissingMandatoryField({
          name: PROPERTY_BINDER_NUMBER_LABEL,
          section: SECTIONS.POLICY_INFORMATION,
        }),
      );
      dispatch(
        removeRemark({
          validation_key: MULTI_LINE_DISCOUNT_VALIDATION_ID,
          item_key: MULTI_LINE_DISCOUNT_ITEM_KEY,
        }),
      );
    }
  }, [isValid, value, dispatch]);

  return (
    <FormControl isRequired>
      <FormLabel ml="0">{PROPERTY_BINDER_NUMBER_LABEL}</FormLabel>
      <Flex
        align="center"
        gap="10px"
        bg={isValid ? 'green.50' : 'yellow.50'}
        border="1px solid"
        borderColor={isValid ? 'green.400' : 'yellow.400'}
        borderRadius="20px"
        pr="16px"
      >
        <Box flex="1">
          <Input
            variant="main"
            id={PROPERTY_BINDER_NUMBER_LABEL}
            name="property_binder_number"
            value={value}
            onChange={(e) =>
              dispatch(
                changePolicyInformation({
                  name: 'property_binder_number',
                  value: e.target.value,
                }),
              )
            }
          />
        </Box>
        {isValid ? (
          <CheckCircleIcon color="green.500" boxSize="20px" />
        ) : (
          <Tooltip
            placement="top"
            label={getValidationMessage(
              validations,
              MULTI_LINE_DISCOUNT_VALIDATION_ID,
            )}
          >
            <span>
              <WarningIcon color="yellow.400" boxSize="20px" />
            </span>
          </Tooltip>
        )}
      </Flex>
      {isInvalid && (
        <Text color="red.500" fontSize="sm" mt="2">
          Property Binder Number must be alphanumeric with at least one number.
          A hyphen is allowed only in the middle.
        </Text>
      )}
    </FormControl>
  );
};

export default PropertyBinderNumberField;
